export const dmarcGenerator = {
    meta: {
        title: "DMARC Record Generator – Domain kostenlos prüfen",
        ogTitle: "",
        description: "Erstellen Sie jetzt Ihren DMARC-Datensatz kostenlos. Kein Login nötig. Sofortschutz vor Spoofing & Phishing."
    },
    "setupBlock": {
        "title": "Setting Up DMARC Is Confusing—We’ll Just Do It For You",
        "subTitle": "Want to skip the hassle? Book a one-time DMARC Setup Call",
        "benefit1": "Full setup by an expert",
        "benefit2": "No ongoing tools or dashboards required",
        "benefit3": "One-and-done—configured correctly from the start",
        "cta": "Book Setup Help"
    },
    pricing: {
        "cta": "Book Setup Help"
    },
    generator: {
        title: "Create Your DMARC Record",
        subTitle: "Quickly generate a DMARC record and apply it to your DNS with ease.",
        form: {
            showMore: "Advanced Configuration",
            domainName: {
                title: "Domain Name",
                info: "The domain name you want to configure for DMARC, SPF, and DKIM policies.",
                placeholder: "Enter domain name"
            },
            version: {
                title: "DMARC Version",
                info: "The DMARC version to be used. Currently only version 1 is available."
            },
            policy: {
                title: "Policy",
                info: "Specifies how emails that do not pass DKIM/SPF are handled: none: delivery without restriction, quarantine: emails are treated as spam, reject: emails are blocked."
            },
            adkim: {
                title: "Adkim",
                info: "Determines the DKIM alignment. With 'strict' (s), the DKIM checks must match exactly. With 'relaxed' (r), slight deviations, such as subdomains, are permitted. Example: adkim=r for relaxed."
            },
            aspf: {
                title: "ASPF",
                info: "Determines the SPF alignment. With 'strict' (s), SPF checks must match exactly. With 'relaxed' (r), slight deviations, such as subdomains, are permitted. Example: aspf=r for relaxed."
            },
            hint: "Please note that aspf and adkim are optional settings. The p= attribute specifies what email servers should do with emails that do not pass SPF and DKIM.",
            subdomainPolicy: {
                title: "Subdomain Policy",
                info: "Defines how email servers should handle messages sent from subdomains of the main domain. It is an extension of the main policy (p) that applies specifically to subdomains. If the sp policy is not set, subdomains automatically inherit the DMARC policy (p) of the main domain. However, the sp policy can be used to define an independent policy for subdomains only."
            },
            percentage: {
                title: "Percentage",
                info: "Specifies the percentage of emails failing DMARC checks that the policy (e.g., quarantine or reject) applies to. Default is 100. Use lower values (e.g., pct=50) to test policies gradually."
            },
            reportInterval: {
                title: "Aggregate Report Interval (in seconds)",
                info: "Defines how often (in seconds) aggregate DMARC reports are sent to the specified rua address. Default is 86400 (24 hours). Adjust for more frequent reporting if needed (e.g., ri=3600 for hourly reports)."
            },
            failureOptions: {
                title: "Failure Options",
                info: "Specifies how forensic (failure) reports are generated when an email fails DMARC checks. Options include: 0: Generate reports if both SPF and DKIM fail (default), 1: Generate reports if either SPF or DKIM fails, d: Generate reports if DKIM fails, s: Generate reports if SPF fails.",
                both: "Both",
                spfOrDkim: "SPF or DKIM"
            },
            reportingFormat: {
                title: "Reporting Format",
                info: "Specifies the format of forensic (failure) reports sent to the email address in the ruf tag. Common values include: afrf: Aggregate Feedback Report Format (default). iodef: Incident Object Description Exchange Format (used for security incident reporting)."
            },
            spfIncludes: {
                title: "SPF Includes",
                placeholder: "e.g., _spf.google.com",
                info: "Allows you to authorize third-party mail servers to send emails on behalf of your domain. This is useful when you use external services (e.g., marketing platforms, CRMs) to send emails."
            },
            dkimSelector: {
                title: "DKIM Selector",
                info: "Specifies the selector used to locate the DKIM public key in DNS. It identifies the specific DKIM key for a domain, allowing multiple keys to coexist.",
                placeholder: "Enter DKIM Selector"
            },
            rua: {
                title: "Aggregate Reports (RUA)",
                info: "Specifies the email address or addresses where DMARC aggregate reports (summary reports) are sent. These reports provide insights into authentication results for your domain's emails. You can add multiple email addresses here.",
                placeholder: "Enter RUA email",
                add: "Add"
            },
            ruf: {
                title: "Forensic Reports (RUF)",
                placeholder: "Enter RUF email",
                info: "Specifies the email address or addresses where DMARC forensic (failure) reports are sent. These reports contain detailed information about individual emails that fail DMARC checks. You can add multiple addresses here",
                add: "Add"
            },
            generate: "Generate DMARC Record",
            "result": {
                "title": "Your DMARC Record",
                "subTitle": "Copy and paste this into your DNS as a TXT record.",
                "copy": "Copy",
                "emailForm": {
                    "title": "Want to learn more about DMARC?",
                    "subtitle": "Enter your email address and we'll send you useful insights and updates.",
                    "placeholder": "e.g. name@example.com",
                    "cta": "Send me DMARC info",
                    "disclaimer": "We’ll only send you useful DMARC-related content. No spam, ever.",
                    acceptTerms: "I accept the terms and conditions"
                }
            },
            error: {
                title: "Error Generating Your DMARC Record",
                noDomainGiven: "Please give a valid domain"
            }
        }
    },
    whySettingUpIsANightMare: {
        title: "Why DIY Can Be a Nightmare",
        subTitle: "Let's face it — setting up a DMARC record is confusing, error-prone, and downright frustrating.",
        list: ["❌ One typo = broken email delivery", "You get raw XML reports made for machines", "❌ Tons of cryptic tags: <code>p=quarantine</code>, <code>aspf=s</code>, <code>fo=1</code>… what do they even mean?", "❌ No alerts, no visibility—until something goes wrong", "❌ DNS formatting is unforgiving — and there's no undo"],
        underTitle: "Want it done right the first time? - Get help from a real person. One-time, no subscription.",
        cta: "Book DMARC Setup"
    },
    whatIsADmarcRecord: {
        title: "What Is a DMARC Record?",
        description: "<div>A <span class='fw-bold'>DMARC (Domain-based Message Authentication, Reporting & Conformance)</span> record is a simple text (TXT) entry you add to your domain's DNS settings. It tells mail servers how to handle emails that <span class='fw-bold'>fail authentication checks</span> like SPF or DKIM.</div> <div class='mt-2'>In plain terms: it's a policy that helps stop spoofing, phishing, and unauthorized email use by telling other servers what to do with suspicious messages—reject, quarantine, or allow them.</div>",
        "explanation": {
            "title": "Example DMARC Record:",
            "recordExample": "v=DMARC1; p=none; rua=mailto:app@dmarcflow.com",
            "entries": {
                "version": "Version",
                "policy": "Policy (none = take no action)",
                "rua": "Email address where reports should be sent"
            },
            "whyNeeded": "Without a DMARC record, your domain may still work — but you'll have no visibility into who's using it, no alerts when spoofing happens, and no control over email abuse.",
            "benefitsTitle": "With a DMARC record in place, you get:",
            "benefits": {
                "0": "Better email security",
                "1": "Insight into unauthorized activity",
                "2": "Stronger trust from mail providers and customers"
            }
        }
    },
    howToCreateADMarcRecord: {
        title: "How to Create a DMARC Record",
        description: "Using DMARCFlow's DMARC Record Generator makes the process quick and simple: 1. Enter your domain and choose the policy you want to apply (learn more about policies here). Add the email addresses for receiving DMARC reports (aggregate and failure reports). Click \"Generate” to create your record. Optional settings are available for advanced customization, but you can skip them and still get a fully functional DMARC record."
    },
    faq: {
        title: "Frequently Asked Questions",
        subTitle: "You have questions? We have answers!",
        list: [
            {
                question: "What Is a DMARC Record Generator?",
                answer: "DMARCFlow's DMARC Record Generator allows you to create a valid DMARC Record in a few clicks. The generated syntax will meet all your specifications and be ready to publish in your DNS."
            },
            {
                question: "How to implement a DMARC record for your domain?",
                answer: "Once the record has been generated, copy it and go to your domain's DNS zone. Add a new TXT or CNAME record, then paste the provided DMARC record. Note: Most DNS providers (e.g. GoDaddy) automatically include the domain name in the Host/Name field, so you only need to enter _dmarc."
            },
            {
                question: "What's the DMARC record format?",
                answer: "TXT is the accepted DMARC record format if you're dealing with it manually. When using DMARCFlow's Managed DMARC solution, the type of DMARC record that should be added to the DNS is a CNAME record. A CNAME (canonical name) record is a DNS record that can create an alias for the domain and allow traffic for the domain name to be redirected elsewhere."
            },
            {
                question: "What is DMARC domain alignment?",
                answer: "Domain alignment is the core DMARC concept. Alignment happens when the domain name in the email's 'From' header matches the sender's email domain. As a result, DMARC passes, indicating that it was a legitimate email. DMARC domain alignment helps protect against spoofing, impersonation attacks, business email compromise, and phishing."
            },
            {
                question: "How does DMARC work with subdomains?",
                answer: "Subdomains inherit DMARC settings from the parent domain unless specified otherwise. For example, if the parent domain has a ‘reject' policy, the subdomain will inherit it. However, if you configure the subdomain separately, the system will not override your manual DMARC settings."
            },
            {
                question: "Can I add a DMARC record without SPF or DKIM?",
                answer: "You can add the record, but it won't function properly. DMARC is based on SPF and DKIM. For it to pass, an email must pass SPF authentication and SPF alignment and/or DKIM authentication and DKIM alignment. If both are missing, DMARC will fail."
            }
        ]
    }
};